import React, { useState } from "react";
import { toast } from "react-toastify";
import { ApiError } from "../../../../services/api.errors";
import { Role, RoleDetail, RolesAPI } from "../../../../services/roles.service";

interface Props {
  isOpen:     boolean;
  onClose:    () => void;
  onCreated:  (role: RoleDetail) => void;
  /** Present in "clone" mode; absent in "create fresh" mode. */
  cloneFrom?: Role | null;
}

const inputCls =
  "w-full border border-gray-200 rounded-lg px-3 py-2 text-base sm:text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent";

export const CreateRoleModal: React.FC<Props> = ({ isOpen, onClose, onCreated, cloneFrom }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  if (!isOpen) return null;

  const close = () => {
    setName("");
    setDescription("");
    setNameError(null);
    onClose();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setNameError("Give the role a name.");
      return;
    }
    setSaving(true);
    setNameError(null);
    try {
      const created = await RolesAPI.createRole({
        name: name.trim(),
        description: description.trim() || null,
        clone_of_role_id: cloneFrom?.role_id ?? null,
      });
      toast.success(cloneFrom ? `Cloned "${cloneFrom.name}" as "${created.name}".` : `Role "${created.name}" created.`);
      onCreated(created);
      close();
    } catch (err) {
      // Backend's error envelope carries only {code, message}, no details.field
      // (see app/core/exceptions.py::domain_error_handler) - key off the HTTP
      // status, not a details code the plumbing doesn't actually populate.
      if (err instanceof ApiError && err.isConflict) {
        setNameError(`A role named "${name.trim()}" already exists.`);
      } else {
        toast.error(err instanceof ApiError ? err.message : "Failed to save the role.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={close}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 px-6 py-5 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              {cloneFrom ? `Clone "${cloneFrom.name}"` : "New Role"}
            </h2>
            {cloneFrom && (
              <p className="text-xs text-gray-500 mt-1">
                Starts with all of {cloneFrom.name}&apos;s permissions - edit them after saving.
              </p>
            )}
          </div>
          <button onClick={close} title="Close" className="p-2 -mr-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg flex-shrink-0">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Role Name</label>
            <input
              className={inputCls}
              value={name}
              onChange={e => { setName(e.target.value); setNameError(null); }}
              placeholder={cloneFrom ? `${cloneFrom.name} (copy)` : "e.g. Senior Receptionist"}
              autoFocus
            />
            {nameError && <p className="text-xs text-red-500 mt-1">{nameError}</p>}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Description (optional)</label>
            <textarea
              className={inputCls + " resize-none"}
              rows={2}
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What this role is for"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
          <button onClick={close} disabled={saving}
            className="px-5 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
            {saving ? "Saving…" : cloneFrom ? "Clone Role" : "Create Role"}
          </button>
        </div>
      </div>
    </div>
  );
};
